/* gd-variant-swap.js — troca de "sabor" na PDP sem recarregar a página, 20/Ago/2026.

   CONTEXTO: snippets/variant-fake.liquid desenha um seletor de "variantes" que na
   verdade são OUTROS produtos (entrelinkagem via custom.product_variations). Cada
   opção é um <a class="tp-variant-link"> apontando pra outra PDP, então trocar de
   sabor custava um page load inteiro.

   O QUE FAZ: intercepta o clique, busca a outra PDP por fetch, troca a .productView
   atual pela da resposta, religa os eventos e atualiza a URL com history.pushState.

   ESCOPO DA TROCA (REGIONS):
     - .productView (snippets/product-page.liquid:468) vai mais longe do que parece: a
       barra sticky de add-to-cart fecha DENTRO dela (o </div> é a linha 1627), então
       galeria, preço, o próprio seletor de sabores, o <monte-seu-combo> e a sticky vêm
       todos no mesmo nó;
     - .productView-bottom (linha 1632) traz as abas/descrição, que mudam por sabor. As
       abas são o custom element <product-tab> (assets/tabs.js:252), que liga os próprios
       listeners no upgrade — entram funcionando sem re-init nenhum.
   Fica FORA: o product-bundle (linha 1630) e o <style> com escopo #ProductSection-main,
   que o Shopify renderiza fora da .productView. Pra incluir mais alguma coisa, é só
   acrescentar o seletor em REGIONS.

   RE-ATTACH: o tema religa a PDP por halo.initProductView($scope) — galeria slick,
   countdown, size chart, "última venda", contador de visitantes, cursor custom, vídeos
   e variant image group. Esse objeto vivia preso no closure do assets/theme.js; abri
   uma saída lá com `window.halo = halo` (uma linha, comentada no arquivo). Sem ela o
   fallback religa só a galeria, via window.haloProductGallery.
   Além disso:
     - custom elements (product-form, variant-radios, quantity-input, monte-seu-combo…)
       fazem upgrade sozinhos ao entrar no documento — não precisam de nada;
     - handlers delegados no document (a maior parte do theme.js) sobrevivem à troca;
     - disparo `shopify:section:load` na section, que é o contrato do tema pra "markup
       novo": product-gallery.js, gd-combo-sticky.js e global.js já escutam;
     - Shopify.PaymentButton.init() volta os botões de checkout dinâmico.

   FALHA: qualquer erro (rede, HTTP != 200, .productView ausente na resposta) cai em
   navegação normal pra mesma URL. O usuário nunca fica travado.

   ─────────────────────────────────────────────────────────────────────────────
   PREFETCH (etapa 2 de performance, 27/Ago/2026)

   O problema que sobrou da v1: o fetch só começava NO CLIQUE. A troca não recarregava
   a página, mas ainda esperava uma ida e volta inteira ao servidor (a PDP é pesada:
   15 sections) antes de qualquer pixel mudar. Na prática o sabor demorava quase o
   mesmo tanto pra aparecer; o que se ganhava era não perder o scroll e não repetir o
   download de CSS/JS.

   Agora o documento chega ANTES do clique, em duas camadas:

     1. INTENÇÃO (pointerover / touchstart / focus no link) — busca E já parseia o
        documento, e ainda aquece a imagem principal do sabor (new Image() com o mesmo
        srcset/sizes, então o navegador reaproveita o download). Entre passar o dedo
        e soltar o clique costumam existir 100-300 ms; é onde a troca fica instantânea.

     2. OCIOSO (requestIdleCallback, quando a régua de sabores entra na tela) — busca
        os sabores restantes e guarda só o HTML em texto, SEM parsear. Documento
        parseado custa memória de verdade (dezenas de MB numa PDP desta); string custa
        pouco. O parse acontece no clique, e mesmo assim sai muito mais barato que a
        rede.

   Guardas: nada de prefetch com economia de dados ligada (navigator.connection.saveData)
   ou em 2G; teto de PREFETCH_MAX documentos no cache; o request de prefetch NUNCA usa
   o AbortController do clique — prefetch não pode cancelar navegação de verdade —, e
   se o clique chega com um prefetch em voo, ele aproveita a mesma promise em vez de
   abrir um segundo request.

   ROLLBACK do prefetch: apagar o bloco "PREFETCH" e as 3 chamadas de onIntent em
   "GATILHOS" (o resto do arquivo volta a funcionar como a v1).
   ROLLBACK de tudo: remover a tag <script> do fim do snippets/variant-fake.liquid.
*/
(function () {
  "use strict";

  var LINK = ".tp-variant-link";
  var LIST = ".tp-variants-tp";
  var REGIONS = [".productView", ".productView-bottom"];
  var BUSY_CLASS = "gd-variant-swapping";
  var PREFETCH_MAX = 8;

  var docs = Object.create(null); // url -> Document já parseado
  var raw = Object.create(null); // url -> HTML em texto, ainda não parseado
  var inflight = Object.create(null); // url -> Promise<string> em voo
  var controller = null; // request do CLIQUE em voo (só ele é abortável)
  var inflightUrl = null; // qual URL o clique está buscando
  var owned = false; // já mexemos no history desta sessão?

  function injectStyle() {
    if (document.getElementById("gd-variant-swap-style")) return;

    var style = document.createElement("style");
    style.id = "gd-variant-swap-style";
    // A régua de sabores segue clicável de propósito: quem se impacientar e clicar
    // outro sabor no meio do carregamento assume o request (o anterior é abortado).
    // Um filho com pointer-events:auto recebe evento mesmo com o pai em none.
    style.textContent =
      "." +
      BUSY_CLASS +
      "{opacity:.55;transition:opacity .15s ease;pointer-events:none}" +
      "." +
      BUSY_CLASS +
      " " +
      LIST +
      "{pointer-events:auto}";
    document.head.appendChild(style);
  }

  function setBusy(state) {
    for (var i = 0; i < REGIONS.length; i++) {
      var view = document.querySelector(REGIONS[i]);
      if (!view) continue;

      view.classList.toggle(BUSY_CLASS, state);
      if (state) view.setAttribute("aria-busy", "true");
      else view.removeAttribute("aria-busy");
    }
  }

  /* ── CACHE E REDE ──────────────────────────────────────────────────────── */

  function cacheSize() {
    return Object.keys(docs).length + Object.keys(raw).length;
  }

  // Um request por URL. Quem chegar depois (clique em cima de um prefetch em voo)
  // recebe a MESMA promise em vez de abrir um segundo request.
  function fetchHtml(url, signal) {
    if (inflight[url]) return inflight[url];

    var request = fetch(url, {
      credentials: "same-origin",
      signal: signal,
      headers: { "X-Requested-With": "gd-variant-swap" },
    })
      .then(function (response) {
        if (!response.ok) throw new Error("HTTP " + response.status);
        return response.text();
      })
      .then(function (html) {
        delete inflight[url];
        return html;
      })
      .catch(function (error) {
        delete inflight[url];
        throw error;
      });

    inflight[url] = request;
    return request;
  }

  function parseInto(url, html) {
    if (!docs[url]) {
      docs[url] = new DOMParser().parseFromString(html, "text/html");
      delete raw[url];
    }
    return docs[url];
  }

  function loadDoc(url) {
    if (docs[url]) return Promise.resolve(docs[url]);
    if (raw[url]) return Promise.resolve(parseInto(url, raw[url]));

    var signal;
    // Só abre um request abortável se não houver nada a caminho. Com prefetch em voo,
    // aproveita ele — abortar aqui mataria o próprio download que vai servir o clique.
    if (!inflight[url]) {
      if (controller) controller.abort();
      controller = new AbortController();
      signal = controller.signal;
    }

    return fetchHtml(url, signal).then(function (html) {
      return parseInto(url, html);
    });
  }

  /* ── PREFETCH ──────────────────────────────────────────────────────────── */

  function shouldPrefetch() {
    var conn =
      navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return true;
    if (conn.saveData) return false;

    var type = conn.effectiveType || "";
    return type.indexOf("2g") === -1;
  }

  // Aquece a foto do sabor que está por vir. Sem isso o HTML chega instantâneo e a
  // imagem — que é o que a pessoa realmente olha — ainda começa do zero no clique.
  // A ordem importa: sizes antes de srcset, senão o navegador escolhe o candidato
  // errado e o download não é reaproveitado pela <img> de verdade.
  function warmImage(doc) {
    var img = doc.querySelector(REGIONS[0] + " img[data-main-image]");
    if (!img) return;

    var warm = new Image();
    var sizes = img.getAttribute("sizes");
    var srcset = img.getAttribute("srcset");
    var src = img.getAttribute("src");

    if (sizes) warm.sizes = sizes;
    if (srcset) warm.srcset = srcset;
    if (src) warm.src = src;
  }

  function prefetch(url, parseNow) {
    if (!shouldPrefetch()) return;
    if (url === window.location.href) return;
    if (docs[url]) return;

    if (raw[url]) {
      if (parseNow) warmImage(parseInto(url, raw[url]));
      return;
    }

    if (inflight[url]) return; // já a caminho
    if (cacheSize() >= PREFETCH_MAX) return;

    // signal undefined de propósito: prefetch não entra na fila do AbortController
    // do clique, e portanto nunca cancela uma navegação de verdade.
    fetchHtml(url, undefined)
      .then(function (html) {
        if (parseNow) warmImage(parseInto(url, html));
        else raw[url] = html;
      })
      .catch(function () {
        /* prefetch é best-effort: falhou, o clique busca de novo */
      });
  }

  function schedule(fn) {
    if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 250);
  }

  // Um sabor por vez, cada um numa janela ociosa diferente: buscar 5 PDPs de uma vez
  // disputaria banda com as imagens da página que a pessoa está vendo agora.
  function prefetchAllIdle() {
    if (!shouldPrefetch()) return;

    var links = document.querySelectorAll(LIST + " " + LINK);
    var index = 0;

    function step() {
      if (index >= links.length) return;

      var url = sameOriginHref(links[index++]);
      if (url) prefetch(url, false);
      schedule(step);
    }

    schedule(step);
  }

  // Só começa quando a régua de sabores está perto da tela: numa PDP em que a pessoa
  // nem chegou a rolar até lá, prefetch é banda jogada fora.
  function watchList() {
    var list = document.querySelector(LIST);
    if (!list) return;

    if (!window.IntersectionObserver) {
      prefetchAllIdle();
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          observer.disconnect();
          prefetchAllIdle();
          return;
        }
      },
      { rootMargin: "300px" }
    );

    observer.observe(list);
  }

  /* ── TROCA ─────────────────────────────────────────────────────────────── */

  // Trocar um nó desse tamanho mexe na altura da página. Guardo onde a régua de
  // sabores estava na viewport e devolvo ela pro mesmo lugar depois da troca, senão
  // o conteúdo pula debaixo do dedo de quem clicou.
  function listOffset() {
    var list = document.querySelector(LIST);
    return list ? list.getBoundingClientRect().top : null;
  }

  function restoreOffset(before) {
    if (before === null) return;
    var after = listOffset();
    if (after === null) return;
    window.scrollBy(0, after - before);
  }

  // O <a> clicado é destruído na troca, e com ele o foco do teclado. Devolve o foco
  // pro equivalente no markup novo (o que ficou marcado como aria-current="page"),
  // sem rolar a página — o restoreOffset já cuidou da posição.
  function refocus(hadFocus) {
    if (!hadFocus) return;

    var selected = document.querySelector(LIST + ' [aria-current="page"]');
    if (selected) selected.focus({ preventScroll: true });
  }

  function swapRegions(doc) {
    var swapped = [];

    for (var i = 0; i < REGIONS.length; i++) {
      var fresh = doc.querySelector(REGIONS[i]);
      var current = document.querySelector(REGIONS[i]);
      if (!fresh || !current) continue;

      // <script> que entra por importNode/replaceWith não executa — e é o que se quer:
      // variants.js, product-form.js e cia já estão carregados.
      current.replaceWith(document.importNode(fresh, true));
      swapped.push(REGIONS[i]);
    }

    return swapped;
  }

  function reinit() {
    var $ = window.jQuery;
    var scope = $ ? $(".halo-productView") : null;

    if (
      window.halo &&
      scope &&
      typeof window.halo.initProductView === "function"
    ) {
      window.halo.initProductView(scope);
    } else if (window.haloProductGallery && scope) {
      window.haloProductGallery.init(scope); // fallback: só a galeria
    }

    var section =
      document.getElementById("shopify-section-main") ||
      document.querySelector('[data-section-type="product"]');

    if (section) {
      section.dispatchEvent(
        new CustomEvent("shopify:section:load", {
          bubbles: true,
          detail: { sectionId: "main", source: "gd-variant-swap" },
        })
      );
    }

    if (typeof window.initializeScrollAnimationTrigger === "function") {
      window.initializeScrollAnimationTrigger();
    }

    if (window.Shopify && window.Shopify.PaymentButton) {
      window.Shopify.PaymentButton.init();
    }

    document.dispatchEvent(
      new CustomEvent("gd:variant-swapped", {
        detail: { url: window.location.href },
      })
    );
  }

  function go(url, push) {
    var before = listOffset();
    var hadFocus =
      document.activeElement && document.activeElement.closest
        ? !!document.activeElement.closest(LINK)
        : false;

    inflightUrl = url;
    setBusy(true);

    return loadDoc(url)
      .then(function (doc) {
        // Sem a região principal a página ficaria meio trocada — pior que navegar.
        if (swapRegions(doc).indexOf(REGIONS[0]) === -1)
          throw new Error(REGIONS[0] + " ausente na resposta");

        document.title = doc.title;

        if (push) {
          // marca a entrada ATUAL antes de empilhar, senão o primeiro "voltar"
          // cairia numa entrada sem state e o popstate não saberia o que fazer
          if (!owned) {
            history.replaceState(
              { gdVariantSwap: window.location.href },
              "",
              window.location.href
            );
            owned = true;
          }
          history.pushState({ gdVariantSwap: url }, "", url);
        }

        setBusy(false);
        reinit();
        restoreOffset(before);
        refocus(hadFocus);

        // A régua nova é outro nó no DOM: reobserva pra seguir pré-buscando os
        // sabores a partir daqui. O cache é por URL, então nada é buscado de novo.
        watchList();
      })
      .catch(function (error) {
        if (error && error.name === "AbortError") return; // clique mais novo assumiu
        console.error("[gd-variant-swap]", error);
        window.location.href = url; // último recurso: navegação de verdade
      })
      .then(function () {
        if (inflightUrl === url) inflightUrl = null;
      });
  }

  /* ── GATILHOS ──────────────────────────────────────────────────────────── */

  // Devolve o href do link só quando ele é um sabor de verdade, do mesmo domínio e
  // diferente do atual. Usado pelo clique e pelos gatilhos de intenção.
  function sameOriginHref(link) {
    if (!link || !link.href) return null;
    if (link.getAttribute("aria-current") === "page") return null;

    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (e) {
      return null;
    }

    if (url.origin !== window.location.origin) return null;
    if (url.href === window.location.href) return null;

    return url.href;
  }

  function linkFrom(target) {
    return target && target.closest ? target.closest(LINK) : null;
  }

  // Sinal de intenção: passar o mouse, encostar o dedo ou tabular até o link. Busca E
  // parseia — aqui o clique é provável, então vale pagar o parse antes.
  function onIntent(event) {
    var url = sameOriginHref(linkFrom(event.target));
    if (url) prefetch(url, true);
  }

  document.addEventListener("pointerover", onIntent, { passive: true });
  document.addEventListener("touchstart", onIntent, { passive: true });
  document.addEventListener("focusin", onIntent);

  document.addEventListener("click", function (event) {
    var link = linkFrom(event.target);
    if (!link) return;

    // deixa passar ctrl/cmd/shift/alt-clique, botão do meio e target=_blank:
    // são pedidos explícitos de "abre em outro lugar"
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      link.target === "_blank"
    ) {
      return;
    }

    var url = sameOriginHref(link);
    if (!url) {
      // mesmo sabor: não navega, mas também não deixa recarregar a página à toa
      if (link.getAttribute("aria-current") === "page") event.preventDefault();
      return;
    }

    event.preventDefault();

    if (inflightUrl === url) return; // já estamos trocando pra esse sabor

    go(url, true);
  });

  window.addEventListener("popstate", function () {
    if (!owned) return; // nunca trocamos nada: histórico não é nosso
    go(window.location.href, false);
  });

  injectStyle();

  if (document.readyState === "complete") watchList();
  else window.addEventListener("load", watchList);
})();
